import api from '@/api/api.js'
// import dayjs from 'dayjs'
import { DataLoadState } from '@/utils/storeUtils.js'
import { eventManager, USER_STATE_CHANGED_EVENT } from '@/eventManager.js'

// Mutation names
const SET_DEVICE_ID = 'setDeviceId'
const SET_GPS_LOAD_STARTED = 'setGPSLoadStarted'
const SET_GPS_LOAD_FINISHED_SUCCESSFULLY = 'setGPSLoadFinishedSuccessfully'
const SET_GPS_LOAD_FAILED = 'setGPSLoadFailed'
const RESET_GPS = 'resetGPS'
const RESET_SUBSCRIPTION = 'resetSubscription'

const SET_ACTIVE_DOG_PROFILE_LOAD_STARTED = 'setActiveDogProfileLoadStarted'
const SET_ACTIVE_DOG_PROFILE_LOAD_FINISHED_SUCCESSFULLY =
  'setActiveDogProfileLoadFinishedSuccessfully'
const SET_ACTIVE_DOG_PROFILE_LOAD_FAILED = 'setActiveDogProfileLoadFailed'

const SET_SUBSCRIPTION_LOAD_STARTED = 'setSubscriptionLoadStarted'
const SET_SUBSCRIPTION_LOAD_FINISHED_SUCCESSFULLY = 'setSubscriptionLoadFinishedSuccessfully'
const SET_SUBSCRIPTION_LOAD_FAILED = 'setSubscriptionLoadFailed'

// Getter names
export const GET_DEVICE_ID = 'getDeviceId'
export const IS_LOADING_GPS = 'isLoadingGPS'
export const GET_GPS = 'getGPS'
export const GET_GPS_LOAD_ERROR = 'getGPSLoadError'

export const IS_LOADING_ACTIVE_DOG_PROFILE = 'isLoadingActiveDogProfile'
export const GET_ACTIVE_DOG_PROFILE = 'getActiveDogProfile'
export const GET_ACTIVE_DOG_PROFILE_LOAD_ERROR = 'getActiveDogProfileLoadError'

export const IS_LOADING_SUBSCRIPTION = 'isLoadingSubscription'
export const GET_SUBSCRIPTION = 'getSubscription'
export const GET_SUBSCRIPTION_LOAD_ERROR = 'getSubscriptionLoadError'

// Action name
export const SET_GPS = 'setGPS'
export const LOAD_GPS_WITH_DEVICE_ID = 'loadGPSWithDeviceId'
export const LOAD_ACTIVE_DOG_PROFILE = 'loadActiveDogProfile'
export const REMOVE_ADMIN = 'removeAdmin'
export const ADD_ADMIN = 'addAdmin'
export const DELETE_GPS = 'deleteGPS'
export const LOAD_SUBSCRIPTION = 'loadSubscription'
export const RECALCULATE_SUBSCRIPTION = 'recalculateSubscription'

// GPS store implementation
export const GPSDeviceStore = {
  namespaced: true,

  state: () => ({
    // state must be a function to allow reuse of this store
    deviceId: null,
    gpsState: new DataLoadState(),
    activeDogProfileState: new DataLoadState(),
    subscriptionState: new DataLoadState()
  }),

  mutations: {
    [SET_DEVICE_ID](state, { deviceId }) {
      state.deviceId = deviceId
    },

    [SET_GPS](state, { gps }) {
      if (!gps || !gps.deviceId) {
        state.deviceId = null
        state.gpsState = new DataLoadState()
        state.activeDogProfileState = new DataLoadState()
        state.subscriptionState = new DataLoadState()
        return
      }

      state.gpsState = new DataLoadState(gps.deviceId).stateForLoadFinished(gps)
      state.activeDogProfileState = new DataLoadState()
      state.subscriptionState = new DataLoadState()
    },

    [SET_GPS_LOAD_STARTED](state, { deviceId }) {
      const dataLoadState = new DataLoadState(deviceId)
      state.gpsState = dataLoadState.stateForLoadStarted()
    },

    [SET_GPS_LOAD_FINISHED_SUCCESSFULLY](state, { gps }) {
      if (gps.deviceId !== state.gpsState.id) {
        // GPS load is stale, ignore
        return
      }

      state.gpsState = state.gpsState.stateForLoadFinished(gps)
    },

    [SET_GPS_LOAD_FAILED](state, { deviceId, error }) {
      if (deviceId !== state.gpsState.id) {
        // GPS load is stale, ignore
        return
      }

      state.gpsState = state.gpsState.stateForLoadFailed(error)
    },

    [RESET_GPS](state, { deviceId }) {
      if (deviceId !== state.gpsState.id) {
        // GPS has changed, ignore
        return
      }

      state.gpsState = new DataLoadState()
      state.activeDogProfileState = new DataLoadState()
      state.subscriptionState = new DataLoadState()
    },

    [RESET_SUBSCRIPTION](state, { deviceId }) {
      if (deviceId !== state.subscriptionState.id) {
        // GPS has changed, ignore
        return
      }

      state.subscriptionState = new DataLoadState()
    },

    [SET_ACTIVE_DOG_PROFILE_LOAD_STARTED](state, { activeDogProfileId }) {
      const dataLoadState = new DataLoadState(activeDogProfileId)
      state.activeDogProfileState = dataLoadState.stateForLoadStarted()
    },

    [SET_ACTIVE_DOG_PROFILE_LOAD_FINISHED_SUCCESSFULLY](state, { activeDogProfileId, dogProfile }) {
      if (activeDogProfileId !== state.activeDogProfileState.id) {
        // Active dog profile load is stale, ignore
        return
      }

      state.activeDogProfileState = state.activeDogProfileState.stateForLoadFinished(dogProfile)
    },

    [SET_ACTIVE_DOG_PROFILE_LOAD_FAILED](state, { activeDogProfileId, error }) {
      if (activeDogProfileId !== state.activeDogProfileState.id) {
        // Active dog profile load is stale, ignore
        return
      }

      state.activeDogProfileState = state.activeDogProfileState.stateForLoadFailed(error)
    },

    [SET_SUBSCRIPTION_LOAD_STARTED](state, { deviceId }) {
      const dataLoadState = new DataLoadState(deviceId)
      state.subscriptionState = dataLoadState.stateForLoadStarted()
    },

    [SET_SUBSCRIPTION_LOAD_FINISHED_SUCCESSFULLY](state, { deviceId, subscription }) {
      if (deviceId !== state.subscriptionState.id) {
        // Subscription load is stale, ignore
        return
      }

      state.subscriptionState = state.subscriptionState.stateForLoadFinished(subscription)
    },

    [SET_SUBSCRIPTION_LOAD_FAILED](state, { deviceId, error }) {
      if (deviceId !== state.subscriptionState.id) {
        // Subscription load is stale, ignore
        return
      }

      state.subscriptionState = state.subscriptionState.stateForLoadFailed(error)
    }
  },

  getters: {
    [GET_DEVICE_ID]: state => {
      return state.deviceId
    },

    [IS_LOADING_GPS]: state => {
      return state.gpsState.status.loading
    },

    [GET_GPS]: state => {
      return state.gpsState.data
    },

    [GET_GPS_LOAD_ERROR]: state => {
      return state.gpsState.status.error
    },

    [IS_LOADING_ACTIVE_DOG_PROFILE]: state => {
      return state.activeDogProfileState.status.loading
    },

    [GET_ACTIVE_DOG_PROFILE]: state => {
      return state.activeDogProfileState.data
    },

    [GET_ACTIVE_DOG_PROFILE_LOAD_ERROR]: state => {
      return state.activeDogProfileState.status.error
    },

    [IS_LOADING_SUBSCRIPTION]: state => {
      return state.subscriptionState.status.loading
    },

    [GET_SUBSCRIPTION]: state => {
      return state.subscriptionState.data
    },

    [GET_SUBSCRIPTION_LOAD_ERROR]: state => {
      return state.subscriptionState.status.error
    }
  },

  actions: {
    [LOAD_GPS_WITH_DEVICE_ID]: async ({ commit, state }, { deviceId }) => {
      if (!deviceId) {
        return
      }

      if (deviceId === state.gpsState.id) {
        // GPS load has already started
        return
      }

      commit(SET_DEVICE_ID, { deviceId })
      commit(SET_GPS_LOAD_STARTED, { deviceId })

      try {
        const response = await api.get(`admin/gps/${deviceId}`)
        const gps = response.data

        commit(SET_GPS_LOAD_FINISHED_SUCCESSFULLY, { gps })
      } catch (error) {
        commit(SET_GPS_LOAD_FAILED, { deviceId, error })
      }
    },

    [SET_GPS]: ({ commit }, { gps }) => {
      var deviceId = null
      if (gps) {
        deviceId = gps.deviceId || null
      }

      commit(SET_DEVICE_ID, { deviceId })
      commit(SET_GPS, { gps })
    },

    [LOAD_ACTIVE_DOG_PROFILE]: async ({ commit, state }) => {
      if (!state.gpsState.data) {
        // GPS has not been loaded yet
        return
      }

      const activeDogProfileId = state.gpsState.data.activeDogProfileId
      if (!activeDogProfileId) {
        // No active dog profile
        return
      }

      if (activeDogProfileId === state.activeDogProfileState.id) {
        // Active dog profile load has already started
        return
      }

      commit(SET_ACTIVE_DOG_PROFILE_LOAD_STARTED, { activeDogProfileId })

      try {
        const response = await api.get(`admin/dogprofile/${activeDogProfileId}`)

        const activeDogProfile = response.data
        commit(SET_ACTIVE_DOG_PROFILE_LOAD_FINISHED_SUCCESSFULLY, {
          activeDogProfileId,
          dogProfile: activeDogProfile
        })
      } catch (error) {
        commit(SET_ACTIVE_DOG_PROFILE_LOAD_FAILED, { activeDogProfileId, error })
      }
    },

    [REMOVE_ADMIN]: ({ commit, state, dispatch }, { deviceId, userId }) => {
      return api.delete(`admin/gps/${deviceId}/admins/${userId}`).then(response => {
        const ownerUserId = state.gpsState.data ? state.gpsState.data.ownerUserId : null

        // Reload GPS data after admin has been removed
        commit(RESET_GPS, { deviceId })
        dispatch(LOAD_GPS_WITH_DEVICE_ID, { deviceId })

        // Emit event to allow other stores to reload or delete remove stale data
        if (ownerUserId) {
          eventManager.$emit(USER_STATE_CHANGED_EVENT, { userId: ownerUserId })
        }

        return response.data
      })
    },

    [ADD_ADMIN]: ({ commit, state, dispatch }, { deviceId, userQuery }) => {
      return api.post(`admin/gps/${deviceId}/admins`, { userQuery }).then(() => {
        const ownerUserId = state.gpsState.data ? state.gpsState.data.ownerUserId : null

        // Reload GPS data after admin has been removed
        commit(RESET_GPS, { deviceId })
        dispatch(LOAD_GPS_WITH_DEVICE_ID, { deviceId })

        // Emit event to allow other stores to reload or delete remove stale data
        if (ownerUserId) {
          eventManager.$emit(USER_STATE_CHANGED_EVENT, { userId: ownerUserId })
        }
      })
    },

    [DELETE_GPS]: ({ commit, state }, { deviceId }) => {
      return api.delete(`admin/gps/${deviceId}`).then(() => {
        if (state.deviceId === deviceId) {
          // Reset GPS data since it has been deleted
          commit(SET_GPS, { gps: null })
        }
      })
    },

    [LOAD_SUBSCRIPTION]: async ({ commit, state }) => {
      if (!state.gpsState.data) {
        // GPS has not been loaded yet
        return
      }

      const deviceId = state.gpsState.data.deviceId
      if (deviceId === state.subscriptionState.id) {
        // GPS load has already started
        return
      }

      commit(SET_SUBSCRIPTION_LOAD_STARTED, { deviceId })

      try {
        const response = await api.get(`admin/gps/${deviceId}/subscription`)
        const subscription = response.data

        commit(SET_SUBSCRIPTION_LOAD_FINISHED_SUCCESSFULLY, { deviceId, subscription })
      } catch (error) {
        if (error && error.message && error.message.includes('status code 404')) {
          commit(SET_SUBSCRIPTION_LOAD_FINISHED_SUCCESSFULLY, { deviceId, subscription: null })
        } else {
          commit(SET_SUBSCRIPTION_LOAD_FAILED, { deviceId, error })
        }
      }
    },

    [RECALCULATE_SUBSCRIPTION]: async ({ commit, state }) => {
      if (!state.gpsState.data) {
        // GPS has not been loaded yet
        return
      }

      const deviceId = state.gpsState.data.deviceId

      commit(RESET_SUBSCRIPTION, { deviceId })
      commit(SET_SUBSCRIPTION_LOAD_STARTED, { deviceId })

      try {
        const response = await api.get(`admin/gps/${deviceId}/subscription/recalculate`)
        const subscription = response.data

        commit(SET_SUBSCRIPTION_LOAD_FINISHED_SUCCESSFULLY, { deviceId, subscription })
      } catch (error) {
        if (error && error.message && error.message.includes('status code 404')) {
          commit(SET_SUBSCRIPTION_LOAD_FINISHED_SUCCESSFULLY, { deviceId, subscription: null })
        } else {
          commit(SET_SUBSCRIPTION_LOAD_FAILED, { deviceId, error })
        }
      }
    }
  }
}
